import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Preloader,
  Link as F7Link,
  f7,
} from 'framework7-react';
import {
  useDeliveryReadiness,
  useDeliveryZones,
  useDeleteDeliveryZone,
  useOrderPauseState,
  useOrderTypeConfig,
  usePauseOrders,
  useUnpauseOrders,
  useUpdateOrderTypeConfig,
} from '../hooks/useOrderSetup';
import { useRestaurantStore, useRestaurantTimezone } from '@/stores/restaurant.store';
import { MapboxZonesPreview } from '../components/MapboxZonesPreview';
import { DeliverySetupWarnings } from '../components/DeliverySetupWarnings';
import { toPolygonRing, zoneColorMap, type ReferenceZone } from '../components/MapboxPolygonEditor';
import type { DeliveryZone, OrderPauseState } from '@restaurant/api-client';
import { targetZoneMode } from '@restaurant/types';
import { formatMoney, inputToMinor, moneyToInput } from '@/utils/money';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { useT } from '@/i18n';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useCan } from '@/hooks/useAbility';
import { useReducer, useRef } from 'react';

// ─── Order type config ────────────────────────────────────────────────────────

function OrderTypeConfigSection({ readOnly }: { readOnly: boolean }) {
  const p = useT().orderSetup.page;
  const { data: configs, isLoading } = useOrderTypeConfig();
  const update = useUpdateOrderTypeConfig();

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const toggleEnabled = (cfg: NonNullable<typeof configs>[number], next: boolean) => {
    if (readOnly) return;
    const label = cfg.orderType === 'PICKUP' ? p.pickup : p.delivery;
    update.mutate(
      { orderType: cfg.orderType, data: { enabled: next } },
      { onSuccess: () => toast(next ? p.toggledOn(label) : p.toggledOff(label)) },
    );
  };

  return (
    <>
      <BlockTitle>{p.orderTypesTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.orderTypesFooter}</BlockFooter>
      {isLoading ? (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      ) : !configs?.length ? (
        <Block strongIos strongMd insetIos insetMd className="text-align-center">
          {p.noConfig}
        </Block>
      ) : (
        <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
          {configs.map((cfg) => {
            const isPickup = cfg.orderType === 'PICKUP';
            return (
              <ListItem
                key={cfg.orderType}
                title={isPickup ? p.pickup : p.delivery}
                subtitle={cfg.enabled ? p.canOrder : p.cannotOrder}
              >
                <i slot="media" className="f7-icons">
                  {isPickup ? 'bag' : 'car_fill'}
                </i>
                <ToggleSwitch
                  slot="after"
                  checked={cfg.enabled}
                  disabled={readOnly || update.isPending}
                  onChange={(v) => toggleEnabled(cfg, v)}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </>
  );
}

// ─── Auto-confirm pay-at-handoff ─────────────────────────────────────────────

// When on, incoming cash / card-on-handoff orders for the channel skip the manual
// Accept and go straight to CONFIRMED (the kitchen ticket prints on arrival).
// Online orders already auto-confirm on payment; bank transfer never does (it
// needs proof review), so this only affects the two pay-at-handoff rails.
function AutoConfirmSection({ readOnly }: { readOnly: boolean }) {
  const p = useT().orderSetup.page;
  const { data: configs, isLoading } = useOrderTypeConfig();
  const update = useUpdateOrderTypeConfig();

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const toggle = (cfg: NonNullable<typeof configs>[number], next: boolean) => {
    if (readOnly) return;
    const label = cfg.orderType === 'PICKUP' ? p.pickup : p.delivery;
    update.mutate(
      { orderType: cfg.orderType, data: { autoConfirmHandoff: next } },
      { onSuccess: () => toast(next ? p.autoConfirmOnToast(label) : p.autoConfirmOffToast(label)) },
    );
  };

  if (isLoading || !configs?.length) return null;

  return (
    <>
      <BlockTitle>{p.autoConfirmTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.autoConfirmHelp}</BlockFooter>
      <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {configs.map((cfg) => {
          const isPickup = cfg.orderType === 'PICKUP';
          return (
            <ListItem
              key={cfg.orderType}
              title={isPickup ? p.pickup : p.delivery}
              subtitle={cfg.autoConfirmHandoff ? p.autoConfirmOn : p.autoConfirmOff}
            >
              <i slot="media" className="f7-icons">
                {isPickup ? 'bag' : 'car_fill'}
              </i>
              <ToggleSwitch
                slot="after"
                checked={cfg.autoConfirmHandoff}
                disabled={readOnly || update.isPending}
                onChange={(v) => toggle(cfg, v)}
              />
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

// ─── Automatic completion ────────────────────────────────────────────────────

// The sweeper closes CONFIRMED orders once the delay past the promised ready
// time has elapsed. Owners who hand every order over themselves turn it off per
// channel; the delay is then never consulted, so it is hidden with the switch.
function AutoCompleteSection({ readOnly }: { readOnly: boolean }) {
  const p = useT().orderSetup.page;
  const { data: configs, isLoading } = useOrderTypeConfig();
  const update = useUpdateOrderTypeConfig();

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const toggle = (cfg: NonNullable<typeof configs>[number], next: boolean) => {
    if (readOnly) return;
    const label = cfg.orderType === 'PICKUP' ? p.pickup : p.delivery;
    update.mutate(
      { orderType: cfg.orderType, data: { autoCompleteEnabled: next } },
      {
        onSuccess: () => toast(next ? p.autoCompleteOnToast(label) : p.autoCompleteOffToast(label)),
      },
    );
  };

  const saveDelay = (cfg: NonNullable<typeof configs>[number], minutes: number) => {
    if (readOnly) return;
    update.mutate(
      { orderType: cfg.orderType, data: { completeStaleAfterMinutesFromReadyAt: minutes } },
      { onError: (e: unknown) => toast(e instanceof Error ? e.message : p.autoCompleteSaveFailed) },
    );
  };

  if (isLoading || !configs?.length) return null;

  const withAutoComplete = configs.filter((cfg) => cfg.autoCompleteEnabled);

  return (
    <>
      <BlockTitle>{p.autoCompleteTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.autoCompleteHelp}</BlockFooter>
      <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {configs.map((cfg) => {
          const isPickup = cfg.orderType === 'PICKUP';
          return (
            <ListItem
              key={cfg.orderType}
              title={isPickup ? p.pickup : p.delivery}
              subtitle={
                cfg.autoCompleteEnabled
                  ? p.autoCompleteOn(cfg.completeStaleAfterMinutesFromReadyAt)
                  : p.autoCompleteOff
              }
            >
              <i slot="media" className="f7-icons">
                {isPickup ? 'bag' : 'car_fill'}
              </i>
              <ToggleSwitch
                slot="after"
                checked={cfg.autoCompleteEnabled}
                disabled={readOnly || update.isPending}
                onChange={(v) => toggle(cfg, v)}
              />
            </ListItem>
          );
        })}
      </List>
      {/* The delay only means anything while the switch is on. */}
      {withAutoComplete.length > 0 && (
        <>
          <BlockTitle>{p.autoCompleteDelayTitle}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{p.autoCompleteDelayFooter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            {withAutoComplete.map((cfg) => (
              <ListInput
                key={cfg.orderType}
                label={cfg.orderType === 'PICKUP' ? p.pickup : p.delivery}
                type="number"
                inputmode="numeric"
                min="1"
                placeholder="90"
                disabled={readOnly}
                defaultValue={String(cfg.completeStaleAfterMinutesFromReadyAt)}
                outline={f7.theme !== 'ios'}
                floatingLabel={f7.theme !== 'ios'}
                // Commit on blur, not per keystroke: saving mid-typing would
                // persist "1" on the way to "120".
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  const next = Number.parseInt(e.target.value, 10);
                  if (!Number.isFinite(next) || next < 1) return;
                  if (next !== cfg.completeStaleAfterMinutesFromReadyAt) saveDelay(cfg, next);
                }}
              />
            ))}
          </List>
        </>
      )}
    </>
  );
}

// ─── Minimum order ────────────────────────────────────────────────────────────

function MinimumOrderSection({ readOnly }: { readOnly: boolean }) {
  const p = useT().orderSetup.page;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data: configs, isLoading } = useOrderTypeConfig();
  const update = useUpdateOrderTypeConfig();

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const edit = (cfg: NonNullable<typeof configs>[number]) => {
    if (readOnly) return;
    const orderType = cfg.orderType;
    const label = orderType === 'PICKUP' ? p.pickup : p.delivery;
    f7.dialog.prompt(
      p.minPrompt(label.toLowerCase()),
      p.minDialogTitle,
      (value) => {
        const minOrderCents = inputToMinor(value, currency);
        update.mutate(
          { orderType, data: { minOrderCents } },
          {
            onSuccess: () =>
              toast(
                minOrderCents > 0
                  ? p.minSet(label, formatMoney(minOrderCents, currency))
                  : p.minRemoved(label),
              ),
          },
        );
      },
      undefined,
      moneyToInput(cfg.minOrderCents, currency),
    );
  };

  return (
    <>
      <BlockTitle>{p.minimumOrderTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.minimumOrderFooter}</BlockFooter>
      {isLoading ? (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      ) : !configs?.length ? (
        <Block strongIos strongMd insetIos insetMd className="text-align-center">
          {p.noConfig}
        </Block>
      ) : (
        <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
          {configs.map((cfg) => {
            const isPickup = cfg.orderType === 'PICKUP';
            return (
              <ListItem
                key={cfg.orderType}
                link={readOnly || update.isPending ? undefined : '#'}
                onClick={readOnly || update.isPending ? undefined : () => edit(cfg)}
                title={isPickup ? p.pickup : p.delivery}
                subtitle={cfg.minOrderCents > 0 ? p.smallestBasket : p.noMinimum}
              >
                <i slot="media" className="f7-icons">
                  {isPickup ? 'bag' : 'car_fill'}
                </i>
                <span slot="after" style={{ fontWeight: 600 }}>
                  {cfg.minOrderCents > 0 ? formatMoney(cfg.minOrderCents, currency) : '-'}
                </span>
              </ListItem>
            );
          })}
        </List>
      )}
    </>
  );
}

// ─── Availability (pause / resume) ────────────────────────────────────────────

function AvailabilitySection() {
  const p = useT().orderSetup.page;
  const tz = useRestaurantTimezone();
  const { data: states, isLoading } = useOrderPauseState();
  const { data: configs } = useOrderTypeConfig();
  const pause = usePauseOrders();
  const unpause = useUnpauseOrders();
  const busy = pause.isPending || unpause.isPending;
  // Anchor each channel's action popup to its own LEFT icon (not the full-width
  // row, which would centre it) so it pops up on the left next to the row.
  const iconRefs = useRef<Record<string, HTMLElement | null>>({});

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const stateFor = (orderType: 'PICKUP' | 'DELIVERY'): OrderPauseState =>
    states?.find((s) => s.orderType === orderType) ?? {
      orderType,
      paused: false,
      mode: null,
      pausedUntil: null,
    };

  // A channel turned OFF above can't be "Live" - its status reflects the toggle,
  // not the pause state (you can't pause/resume something that's disabled).
  const enabledFor = (orderType: 'PICKUP' | 'DELIVERY'): boolean =>
    configs?.find((c) => c.orderType === orderType)?.enabled ?? true;

  const describe = (s: OrderPauseState): string => {
    if (!s.paused) return p.accepting;
    if (s.mode === 'UNTIL_CLOSE') return p.pausedUntilClose;
    if (s.pausedUntil) {
      const time = new Date(s.pausedUntil).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: tz,
      });
      return p.pausedResumes(time);
    }
    return p.paused;
  };

  const openActions = (s: OrderPauseState) => {
    const orderType = s.orderType;
    const label = orderType === 'PICKUP' ? p.pickup : p.delivery;
    const pauseFor = (mode: 'TIMED' | 'UNTIL_CLOSE', durationMinutes?: number) =>
      pause.mutate(
        { orderType, mode, durationMinutes },
        {
          onSuccess: () =>
            toast(
              mode === 'UNTIL_CLOSE'
                ? p.pausedUntilCloseToast(label)
                : p.pausedForToast(label, durationMinutes),
            ),
        },
      );

    type ActionButton = {
      text: string;
      label?: boolean;
      color?: string;
      onClick?: () => void;
    };
    const group: ActionButton[] = [
      { label: true, text: p.orderingLabel(label) },
      { text: p.pause30, onClick: () => pauseFor('TIMED', 30) },
      { text: p.pause1Hour, onClick: () => pauseFor('TIMED', 60) },
      { text: p.pauseHours(2), onClick: () => pauseFor('TIMED', 120) },
      { text: p.pauseHours(3), onClick: () => pauseFor('TIMED', 180) },
      { text: p.pauseHours(4), onClick: () => pauseFor('TIMED', 240) },
      { text: p.pauseUntilClose, onClick: () => pauseFor('UNTIL_CLOSE') },
    ];
    if (s.paused) {
      group.push({
        text: p.resumeNow,
        color: 'green',
        onClick: () =>
          unpause.mutate({ orderType }, { onSuccess: () => toast(p.resumedToast(label)) }),
      });
    }

    f7.actions
      .create({
        // Override the global "anchor to last-pressed row" default so the popup
        // points at this channel's left icon and opens on the left.
        targetEl: iconRefs.current[orderType] ?? undefined,
        buttons: [[...group, { text: p.cancel, color: 'red' }]],
      })
      .open();
  };

  return (
    <>
      <BlockTitle>{p.availabilityTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.availabilityFooter}</BlockFooter>
      <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {(['PICKUP', 'DELIVERY'] as const).map((orderType) => {
          const isPickup = orderType === 'PICKUP';
          const s = stateFor(orderType);
          const enabled = enabledFor(orderType);
          // Disabled channels aren't interactive (nothing to pause) and read as
          // grey "Off"; the pause/resume popup only makes sense while enabled.
          return (
            <ListItem
              key={orderType}
              link={busy || !enabled ? undefined : '#'}
              noChevron
              onClick={busy || !enabled ? undefined : () => openActions(s)}
              title={isPickup ? p.pickup : p.delivery}
              subtitle={!enabled ? p.cannotOrder : describe(s)}
            >
              <i
                slot="media"
                ref={(el) => {
                  iconRefs.current[orderType] = el;
                }}
                className="f7-icons"
              >
                {isPickup ? 'bag' : 'car_fill'}
              </i>
              <span
                slot="after"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  color: isLoading
                    ? 'var(--f7-list-item-subtitle-text-color)'
                    : !enabled
                      ? 'var(--f7-list-item-subtitle-text-color)'
                      : s.paused
                        ? '#c8780a'
                        : '#248a3d',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: isLoading
                      ? '#c7c7cc'
                      : !enabled
                        ? '#c7c7cc'
                        : s.paused
                          ? '#ff9500'
                          : '#34c759',
                  }}
                />
                {isLoading ? '…' : !enabled ? p.disabled : s.paused ? p.paused : p.live}
              </span>
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

// ─── Delivery zones ───────────────────────────────────────────────────────────

function DeliveryZonesSection({
  readOnly,
  onAdd,
  onEdit,
  addLabel,
  title,
  emptyText,
  footer,
  zoneModes,
  mapPreview,
}: {
  readOnly: boolean;
  onAdd: () => void;
  onEdit: (zone: DeliveryZone) => void;
  // Labels default to the map-ZONE wording; the by-distance variant overrides
  // them so its list is never called "delivery zones" (would confuse the two).
  addLabel?: string;
  title?: string;
  emptyText?: string;
  footer?: string;
  // Which DeliveryZone shapes this list owns. ALL shapes share one table, so the
  // list MUST filter by mode - otherwise map polygons leak into the distance-band
  // list (and vice versa) even though pricing only ever uses the active mode's.
  zoneModes?: DeliveryZone['mode'][];
  // ZONE mode only: render a read-only map above the list showing every drawn
  // polygon area at once. Off for distance bands (they have no drawn shape).
  mapPreview?: boolean;
}) {
  const p = useT().orderSetup.page;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const lat = useRestaurantStore((s) => s.restaurantConfig?.latitude);
  const lng = useRestaurantStore((s) => s.restaurantConfig?.longitude);
  const { data: allZones, isLoading } = useDeliveryZones();
  // Retired zones (isActive: false) must NOT render here. The list endpoint
  // returns them, and filtering on `mode` alone once showed an owner five healthy
  // -looking bands that the resolver ignored, while the setup warning correctly
  // said there were none — the screen contradicted the banner above it. Match
  // what the quote path actually consults: active zones of the live shape.
  const live = (allZones ?? []).filter((z) => z.isActive);
  const zones = zoneModes ? live.filter((z) => zoneModes.includes(z.mode)) : live;
  const deleteZone = useDeleteDeliveryZone();

  // Colored reference areas for the preview map - same color assignment as the
  // editor (POLYGON zones by displayOrder), so a zone's color is stable across
  // the map, the editor, and this list.
  const previewZones: ReferenceZone[] = mapPreview
    ? (() => {
        const colorByZoneId = zoneColorMap(
          (zones ?? [])
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((z) => z.id),
        );
        return (zones ?? [])
          .map((z) => ({
            id: z.id,
            label: z.label,
            ring: toPolygonRing(z.polygon),
            color: colorByZoneId.get(z.id) ?? '#3bb2d0',
          }))
          .filter((z): z is ReferenceZone => z.ring != null && z.ring.length >= 3);
      })()
    : [];
  const center = lat != null && lng != null ? { latitude: lat, longitude: lng } : null;

  const handleDelete = (zoneId: string, label: string) => {
    if (readOnly) return;
    f7.dialog.confirm(p.deleteZoneConfirm(label), p.deleteZoneTitle, () => {
      deleteZone.mutate(zoneId, {
        onSuccess: () =>
          f7.toast.show({ text: p.zoneDeleted, closeTimeout: 2000, position: 'bottom' }),
      });
    });
  };

  return (
    <>
      <BlockTitle>{title ?? p.deliveryZonesTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{footer ?? p.zonesFooter}</BlockFooter>
      {mapPreview && previewZones.length > 0 && (
        <Block
          strongIos
          strongMd
          insetIos
          insetMd
          className="no-padding"
          style={{ overflow: 'hidden' }}
        >
          <MapboxZonesPreview
            // Re-mount (and re-fit) whenever a zone's shape changes, so an edit
            // or add/delete is reflected. Cheap signature: id + point count +
            // first vertex is enough to detect any reshape for a few zones.
            key={previewZones
              .map((z) => `${z.id}:${z.ring.length}:${z.ring[0]?.join(',')}`)
              .join('|')}
            zones={previewZones}
            center={center}
          />
        </Block>
      )}
      {isLoading ? (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      ) : !zones?.length ? (
        <Block strongIos strongMd insetIos insetMd className="text-align-center">
          {emptyText ?? p.noZones}
        </Block>
      ) : (
        <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
          {zones.map((zone) => {
            const reach =
              zone.mode === 'POLYGON'
                ? p.mapArea
                : zone.maxDistanceKm != null
                  ? p.within(zone.maxDistanceKm)
                  : p.noRangeSet;
            return (
              <ListItem
                key={zone.id}
                className="zone-tier-row"
                link={readOnly ? undefined : '#'}
                onClick={readOnly ? undefined : () => onEdit(zone)}
                title={zone.label}
                subtitle={reach}
                footer={`${p.bandForm.feeLabel}: ${formatMoney(zone.feeCents, currency)} · ${p.bandForm.minOrderLabel}: ${formatMoney(zone.minOrderCents, currency)}`}
              >
                <i slot="media" className="f7-icons">
                  placemark
                </i>
                {/* Native <button>, not F7Link/F7Button - BOTH of those render an
                    <a>, and the row itself is already an <a> (link="#"); an <a>
                    inside an <a> is invalid DOM (React hydration warning). Strip
                    the native button chrome inline (F7's `.link` only resets an
                    <a>, so on a <button> the UA border/background box shows). */}
                {!readOnly && (
                  <button
                    slot="after"
                    type="button"
                    className="link icon-only color-red"
                    style={{ border: 'none', background: 'transparent', padding: 8 }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleDelete(zone.id, zone.label);
                    }}
                  >
                    <i className="f7-icons">trash</i>
                  </button>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
      {!readOnly && (
        <List strongIos strongMd insetIos insetMd>
          <ListItem>
            <F7Link slot="title" onClick={onAdd} style={{ color: 'var(--f7-theme-color)' }}>
              {addLabel ?? p.addZone}
            </F7Link>
          </ListItem>
        </List>
      )}
    </>
  );
}

// ─── Delivery pricing mode ────────────────────────────────────────────────────

// Owner switch (FRONTEND_PRACTICES Rule 11): "how do you price delivery -
// by distance or by map zones?". Persists on the DELIVERY OrderTypeConfig; the
// raw per-zone `mode` discriminator stays hidden (zones inherit it from here).
function DeliveryPricingModeSection({ readOnly }: { readOnly: boolean }) {
  const p = useT().orderSetup.page;
  const { data: configs, isLoading } = useOrderTypeConfig();
  const { data: allZones } = useDeliveryZones();
  const { data: readiness } = useDeliveryReadiness();
  const update = useUpdateOrderTypeConfig();
  const delivery = configs?.find((c) => c.orderType === 'DELIVERY');
  // Cancelling the confirm dialog leaves the config unchanged, so the query data
  // (which drives each radio's `checked`) is identical — force a re-render so the
  // radio the owner tapped snaps back to the still-active mode.
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  const priceMode = delivery?.deliveryPriceMode ?? 'DISTANCE';
  const distanceCalc = delivery?.distanceCalculation ?? 'DIRECT';
  const lat = useRestaurantStore((s) => s.restaurantConfig?.latitude);
  const lng = useRestaurantStore((s) => s.restaurantConfig?.longitude);
  const isPinned = lat != null && lng != null;

  const save = (data: {
    deliveryPriceMode?: 'DISTANCE' | 'ZONE';
    distanceCalculation?: 'DIRECT' | 'ROUTE';
    deliveryBufferMinutes?: number;
  }) => {
    if (readOnly) return;
    update.mutate(
      { orderType: 'DELIVERY', data },
      {
        onError: (e: unknown) =>
          f7.dialog.alert(e instanceof Error ? e.message : p.deliveryPricingSaveFailed),
      },
    );
  };

  // Warn (and only actually switch on confirm) when active zones of the OTHER
  // method would be stranded by this change; otherwise switch straight away.
  //
  // This is a PREVIEW only. Retiring the stranded zones is the server's job, done
  // in the same transaction as the config write (UpsertOrderTypeConfigUseCase),
  // because only the server knows what the merged config ends up being: this
  // screen patches one field at a time, so deriving the target here and acting on
  // it retired the very bands the saved config still needed. `priceMode` and
  // `distanceCalc` below are the SERVER's current values, so the preview matches
  // what the server will decide — but if they ever disagree, the server wins and
  // the invalidated queries re-render the truth.
  const saveWithConfirm = (data: {
    deliveryPriceMode?: 'DISTANCE' | 'ZONE';
    distanceCalculation?: 'DIRECT' | 'ROUTE';
  }) => {
    if (readOnly) return;
    const nextPriceMode = data.deliveryPriceMode ?? priceMode;
    const nextDistanceCalc = data.distanceCalculation ?? distanceCalc;
    // Driving distance is measured FROM the restaurant, so an unpinned one makes
    // every delivery fail. Refuse the switch instead of letting the owner save a
    // setup that only breaks later, at a customer's checkout.
    if (nextPriceMode === 'DISTANCE' && nextDistanceCalc === 'ROUTE' && !isPinned) {
      f7.dialog.alert(p.pinRequiredForRoute, p.pinRequiredForRouteTitle, () => forceRender());
      return;
    }
    const target = targetZoneMode(nextPriceMode, nextDistanceCalc);
    const stranded = (allZones ?? []).filter((z) => z.isActive && z.mode !== target);
    if (stranded.length === 0) {
      save(data);
      return;
    }
    f7.dialog.confirm(
      p.switchModeConfirm,
      p.switchModeConfirmTitle,
      // Confirm: save the switch. The server retires the stranded zones with it.
      () => save(data),
      // Cancel: keep the current mode; re-render to revert the tapped radio.
      () => forceRender(),
    );
  };

  if (isLoading) {
    return (
      <Block className="text-align-center">
        <Preloader />
      </Block>
    );
  }

  return (
    <>
      {/* Above the settings, not below them: an owner who is about to change the
          pricing mode needs to see what is already broken first. */}
      <DeliverySetupWarnings readiness={readiness} />
      <BlockTitle>{p.deliveryPricingTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.deliveryPricingFooter}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          radio
          name="delivery-price-mode"
          value="DISTANCE"
          disabled={readOnly}
          checked={priceMode === 'DISTANCE'}
          title={p.byDistance}
          footer={p.byDistanceFooter}
          onChange={() => saveWithConfirm({ deliveryPriceMode: 'DISTANCE' })}
        />
        <ListItem
          radio
          name="delivery-price-mode"
          value="ZONE"
          disabled={readOnly}
          checked={priceMode === 'ZONE'}
          title={p.byMapZones}
          footer={p.byMapZonesFooter}
          onChange={() => saveWithConfirm({ deliveryPriceMode: 'ZONE' })}
        />
      </List>

      {priceMode === 'DISTANCE' && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              radio
              name="distance-calc"
              value="DIRECT"
              disabled={readOnly}
              checked={distanceCalc === 'DIRECT'}
              title={p.straightLine}
              footer={p.straightLineFooter}
              onChange={() => saveWithConfirm({ distanceCalculation: 'DIRECT' })}
            />
            <ListItem
              radio
              name="distance-calc"
              value="ROUTE"
              disabled={readOnly}
              checked={distanceCalc === 'ROUTE'}
              title={p.drivingDistance}
              footer={p.drivingDistanceFooter}
              onChange={() => saveWithConfirm({ distanceCalculation: 'ROUTE' })}
            />
          </List>
          {/* Only meaningful against a live estimate: the static modes' zone
              number is already the owner's promise, buffer included. */}
          {distanceCalc === 'ROUTE' && (
            <>
              <BlockTitle>{p.dispatchBufferTitle}</BlockTitle>
              <BlockFooter style={{ marginTop: -6 }}>{p.dispatchBufferFooter}</BlockFooter>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                <ListInput
                  label={p.dispatchBufferLabel}
                  type="number"
                  inputmode="numeric"
                  min="0"
                  placeholder="0"
                  disabled={readOnly}
                  defaultValue={String(delivery?.deliveryBufferMinutes ?? 0)}
                  outline={f7.theme !== 'ios'}
                  floatingLabel={f7.theme !== 'ios'}
                  // Commit on blur, not per keystroke: this is a config write, and
                  // saving mid-typing would persist "1" on the way to "15".
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    const next = Number.parseInt(e.target.value, 10);
                    const value = Number.isFinite(next) && next >= 0 ? next : 0;
                    if (value !== (delivery?.deliveryBufferMinutes ?? 0)) {
                      save({ deliveryBufferMinutes: value });
                    }
                  }}
                />
              </List>
            </>
          )}
        </>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OrderSetupPage() {
  const t = useT();
  const settingsBackLink = useSettingsDetailBackLink();
  const readOnly = !useCan('manage', 'OrderTypeConfig');
  // Editors slide in as full-width pushed pages (FRONTEND_PRACTICES Rule 12).
  const nav = (path: string) => f7.views.current.router.navigate(path);
  const { data: configs } = useOrderTypeConfig();
  const deliveryCfg = configs?.find((c) => c.orderType === 'DELIVERY');
  const priceMode = deliveryCfg?.deliveryPriceMode ?? 'DISTANCE';
  const distanceCalc = deliveryCfg?.distanceCalculation ?? 'DIRECT';

  // POLYGON zones edit on the map; distance tiers use the simple form.
  const editZone = (zone: DeliveryZone) =>
    nav(
      zone.mode === 'POLYGON'
        ? `/settings/orders/delivery-zones/map/${zone.id}`
        : `/settings/orders/delivery-zones/${zone.id}`,
    );

  return (
    <Page name="order-setup">
      <Navbar title={t.settingsFeature.orderSetup} backLink={settingsBackLink} />

      {readOnly && (
        <Block strongIos strongMd insetIos insetMd data-testid="order-setup-read-only">
          {t.orderSetup.page.readOnlyNotice}
        </Block>
      )}
      <OrderTypeConfigSection readOnly={readOnly} />
      <AvailabilitySection />
      <AutoConfirmSection readOnly={readOnly} />
      <AutoCompleteSection readOnly={readOnly} />
      <MinimumOrderSection readOnly={readOnly} />
      <DeliveryPricingModeSection readOnly={readOnly} />
      {/* Pricing config per model: ZONE → map polygons; DISTANCE+DIRECT →
          straight-line distance bands; DISTANCE+ROUTE → driving-distance bands. */}
      {priceMode === 'ZONE' && (
        <DeliveryZonesSection
          readOnly={readOnly}
          zoneModes={['POLYGON']}
          mapPreview
          addLabel={t.orderSetup.page.drawArea}
          onAdd={() => nav('/settings/orders/delivery-zones/map/new')}
          onEdit={editZone}
        />
      )}
      {priceMode === 'DISTANCE' && distanceCalc === 'DIRECT' && (
        <DeliveryZonesSection
          readOnly={readOnly}
          zoneModes={['STRAIGHT_LINE']}
          title={t.orderSetup.page.distanceBandsTitle}
          addLabel={t.orderSetup.page.addDistanceBand}
          emptyText={t.orderSetup.page.noDistanceBands}
          footer={t.orderSetup.page.distanceBandsFooter}
          onAdd={() => nav('/settings/orders/delivery-zones/new')}
          onEdit={editZone}
        />
      )}
      {priceMode === 'DISTANCE' && distanceCalc === 'ROUTE' && (
        <DeliveryZonesSection
          readOnly={readOnly}
          zoneModes={['ROUTE_DISTANCE']}
          title={t.orderSetup.page.distanceBandsTitle}
          addLabel={t.orderSetup.page.addDistanceBand}
          emptyText={t.orderSetup.page.noDistanceBands}
          footer={t.orderSetup.page.drivingBandsFooter}
          onAdd={() => nav('/settings/orders/delivery-zones/new')}
          onEdit={editZone}
        />
      )}
    </Page>
  );
}
